import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface TourStyleCardProps {
  headerIcon: ReactNode;
  headerLabel: string;
  title: string;
  onDismiss?: () => void;
  dismissLabel?: string;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  maxWidthClassName?: string;
  /** When true, card flows in a parent flex/scrim instead of fixed upper-third placement. */
  embedded?: boolean;
}

/** Product-tour card shell — gradient header, rounded body, spring entrance. */
export function TourStyleCard({
  headerIcon,
  headerLabel,
  title,
  onDismiss,
  dismissLabel = 'Dismiss',
  children,
  footer,
  className = '',
  maxWidthClassName = 'max-w-md',
  embedded = false,
}: TourStyleCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      className={
        embedded
          ? `relative w-full pointer-events-auto ${maxWidthClassName} ${className}`
          : `fixed z-[100] inset-x-3 bottom-[max(1rem,env(safe-area-inset-bottom))] sm:inset-x-4 sm:bottom-auto sm:top-[33%] mx-auto pointer-events-auto max-h-[min(88dvh,calc(100dvh-1.5rem))] ${maxWidthClassName} ${className}`
      }
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-style-card-title"
    >
      <div className="flex max-h-[inherit] flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-2xl dark:border-gray-700/60 dark:bg-gray-900 dark:shadow-black/40">
        <div className="flex shrink-0 items-center justify-between rounded-t-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2.5">
            {headerIcon}
            <span className="truncate text-sm font-semibold text-white">{headerLabel}</span>
          </div>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="shrink-0 p-0.5 text-white/70 transition-colors hover:text-white"
              aria-label={dismissLabel}
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <p id="tour-style-card-title" className="mb-1 text-sm font-medium text-gray-900 dark:text-white">
            {title}
          </p>
          {children}
          {footer}
        </div>
      </div>
    </motion.div>
  );
}
